import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs/internal/Observable';
import { getFileTypes } from 'src/app/inventory/interfaces/file-types-list';
import { IFileUploadObject } from 'src/app/shared/components/file-uploader/file-upload-object.interface';
import { InputComponent } from 'src/app/shared/components/input/input.component';
import { IOptionItem } from 'src/app/shared/interfaces/option-item.interface';
import { OrderTypeEnum } from 'src/app/shared/interfaces/OrderTypeEnum';
import { ISaveAttachedFileEvent } from 'src/app/shared/interfaces/save-attached-file-event.interface';
import { FormattedDatePipe } from 'src/app/shared/pipes/formatted-date/formatted-date.pipe';
import { FileService } from 'src/app/shared/services/file-service';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { getDateFromISO } from '../../../../shared/helpers/helpers';
import { IEdiOrder } from '../../../../shared/interfaces/EDI-order.interface';
import { INDC } from '../../../../shared/interfaces/INDC.interface';
import {
  DecrementProcessedOrdersCount,
  GetEDIAttachments,
  GetEDIOrder,
  ReceiveEDIOrder,
} from '../../../../shared/states/orders/orders.actions';
import { OrdersState } from '../../../../shared/states/orders/orders.state';
import { PdfSignatureService } from 'src/app/shared/services/pdf-signature-service';
import { CSOSRequiredValidator } from 'src/app/shared/validators/CSOS.validator';

@UntilDestroy()
@Component({
  selector: 'app-receive-order-table',
  templateUrl: './receive-order-table.component.html',
  styleUrls: ['./receive-order-table.component.scss', '../../../inventory.scss'],
})
export class ReceiveOrderTableComponent implements OnInit, OnDestroy {
  @Select(OrdersState.orderFiles) orderFiles$: Observable<IFileUploadObject[]>;
  @Select(OrdersState.receiveEDI) receiveEDI$: Observable<IEdiOrder>;
  @Select(OrdersState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(OrdersState.recentRequestedAttachmentMetadata) recentRequestedAttachmentURL$: Observable<string | undefined>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;
  
  fileTypes: IOptionItem[] = getFileTypes(OrderTypeEnum.Purchase);

  getDateFromISO = getDateFromISO;

  tableOptions = {
    asc: false,
  };

  receivingValue: number[] = [];

  editRow: boolean[] = [];
  receiveEDI: IEdiOrder;

  showPinDialog: boolean;
  showResetPinDialog: boolean;
  focusPin: boolean;

  navigation: any;

  isEditMode: boolean;

  orderFiles: IFileUploadObject[] = [];
  updatedAttachments: IFileUploadObject[] = [];
  signature: string;

  isAttachmentSavingMode: boolean;

  form: UntypedFormGroup;

  @ViewChild('csos') csosElement;
  @ViewChild('location') locationElement;
  @ViewChild('transactionDatePicker') transactionDateElement;
  @ViewChild('receiveButton') receiveButtonElement;
  @ViewChildren('appInput') appInputs: QueryList<InputComponent>;
  @ViewChild('fileAttachmentWrapper') fileAttachmentWrapper;
  @ViewChild('buyerSupplier') buyerSupplier;
  tableHeight: string;

  constructor(
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private formattedDatePipe: FormattedDatePipe,
    private fileService: FileService,
    private pdfSignatureService: PdfSignatureService, 
  ) {
    this.navigation = router.getCurrentNavigation();
    this.form = fb.group({
      transactionDateDisplayValue: [formattedDatePipe.transform(new Date(), 'MMM d a, HH:mm yyyy'), Validators.required],
      transactionDate: [new Date(), Validators.required],
      inventoryLocationId: this.store.selectSnapshot(InventoryState.primaryLocationID),
      csos: ''
    });
  }

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.params?.id;
    id ?
      this.store.dispatch(new GetEDIOrder(id)) :
      this.store.dispatch(new GetEDIOrder());

    this.receiveEDI$
      .pipe(untilDestroyed(this))
      .subscribe((receiveObj: IEdiOrder) => {
        if (!receiveObj) {
          return;
        }
        this.receiveButtonElement?.focus();
        if (receiveObj.defaultInventoryLocationID) {
          this.form.get('inventoryLocationId').setValue(receiveObj.defaultInventoryLocationID);
        }
        this.receiveEDI = receiveObj;
        const csos = this.form.get('csos');
        csos.patchValue(this.receiveEDI?.csos);
        csos.addValidators(CSOSRequiredValidator.bind(this, this.receiveEDI?.orderLineItems));
        this.editRow = Array(this.receiveEDI?.orderLineItems?.length || 0).fill(false);
      });

    this.store.dispatch(new GetEDIAttachments(null));

    this.orderFiles$
      .pipe(untilDestroyed(this))
      .subscribe((files: IFileUploadObject[]) => {
        this.orderFiles = files;
        setTimeout(() => {
          this.initTableHeightString();
        });

      });
  }

  tabbingStartOver(): void {
    this.csosElement?.focusInput();
  }

  openEditMode(index: number): void {
    this.editRow[index] = true;
    this.receivingValue[index] = this.receiveEDI.orderLineItems[index].receiving || 0;

    setTimeout(() => {
      const receivingInputCmp = document.getElementById('receiving_' + index);

      if (receivingInputCmp) {
        receivingInputCmp.getElementsByTagName('input')?.item(0)?.select();
      }
    });
  }

  closeEditMode(i: number, save: boolean = true): void {
    if (save && +this.receivingValue[i] >= 0) {
      this.receiveEDI = {
        ...this.receiveEDI,
        orderLineItems: this.receiveEDI.orderLineItems
          .map((item: INDC, index: number) => index === i ? {...item, receiving: +this.receivingValue[i]} : item),
      };
    }

    this.editRow[i] = false;
    this.receivingValue[i] = null;
    this.editValueInputTabKeyDownKeyUpHandler(i);
  }

  openPinDialog(): void {
    this.showPinDialog = true;
    this.focusPin = true;
  }

  closePinDialog(): void {
    this.showPinDialog = false;
  }

  anyEditModeOpen(): boolean {
    return this.editRow.some(el => el === true);
  }

  async validateRequest({success, pin, response}): Promise<void> {
    if (success) {
      if (this.isAttachmentSavingMode) {
        //sign uploaded
        if (this.signature) {
          const name = response?.user?.firstName + ' ' + response?.user?.lastName;
          await this.pdfSignatureService.sign(
            this.updatedAttachments, 
            this.signature, 
            name, 
            this.store.selectSnapshot(PharmacyState.currentPharmacy)?.name,
            new Date(),
          );
        }
        this.fileService.saveAttachmentsList(
          {orderId: this.receiveEDI?.id, pin, csos: this.form.get('csos').value},
          this.isEditMode,
          this.orderFiles,
          this.updatedAttachments,
        );
        this.receiveEDI = {...this.receiveEDI, csos: this.form.get('csos').value};
        this.showPinDialog = false;
        this.isAttachmentSavingMode = false;
      } else {
        this.receiveOrder(pin);
      }
    }
  }

  receiveOrder(pin: string): void {
    const receiveEDI = {...this.receiveEDI};
    const raw = this.form.getRawValue();
    receiveEDI.pin = +pin;
    receiveEDI.transactionDate = raw.transactionDate;
    receiveEDI.inventoryLocationId = raw.inventoryLocationId;
    this.store.dispatch(new ReceiveEDIOrder(receiveEDI))
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.showPinDialog = false;
        if (this.store.selectSnapshot(OrdersState.success)) {
          this.store.dispatch(new DecrementProcessedOrdersCount());
          const bulkAdded = receiveEDI?.orderLineItems?.filter(x => x.receiving > 0)?.length;
          this.router.navigate(['/inventory/order-history'], {state: {bulkAdded}});
        }
      });
  }

  openResetPinDialog(): void {
    this.focusPin = false;
    this.showPinDialog = false;
    this.showResetPinDialog = true;
  }

  closeResetPinDialog(): void {
    this.showResetPinDialog = false;
    this.showPinDialog = true;
    this.focusPin = true;
  }

  afterPinUpdated(success: boolean): void {
    // TODO: show message of pin update
    if (success) {
      this.closeResetPinDialog();
    }
  }

  saveAttachmentsList(event: ISaveAttachedFileEvent): void {
    if (event.attachments) {
      this.isAttachmentSavingMode = true;
      this.showPinDialog = true;
      this.isEditMode = event.isEditMode;
      setTimeout(() => this.focusPin = true);
      this.updatedAttachments = event.attachments;
      this.signature = event.signature;
    }
  }

  downloadFile(item: IFileUploadObject): void {
    this.fileService.downloadFile(item);
  }

  dateTimeChanged(date): void {
    this.form.get('transactionDate')?.setValue(date?.value);
  }
  
  tabFromDatePicker(): void {
   this.appInputs?.first ? this.appInputs.first.focusInput() : this.receiveButtonElement?.focus();
  }

  editValueInputTabKeyDownKeyUpHandler(i): void {
    //no open edit inputs
    if (!this.anyEditModeOpen()) {
      this.receiveButtonElement?.focus();
      return;
    }

    const indexOfNextOpenInput = this.editRow.findIndex((x, index) => index > i && x);

    if (indexOfNextOpenInput > -1) {
      this.appInputs.toArray()[indexOfNextOpenInput]?.focusInput();
    }

    const openEditModeCountAfterCurrent = this.editRow.filter((x, index) => index > i && x)?.length;
    //last open edit input
    if (openEditModeCountAfterCurrent === 0) {
      this.focusFirstFocusableElement();
      return;
    }
  }

  focusFirstFocusableElement(): void {
    event.preventDefault();
     this.locationElement ? this.locationElement.focus() : this.transactionDateElement.focusInput();
  }

  now(): Date {
    return new Date();
  }

  private initTableHeightString(): void {
    const attachmentsElHeight = this.fileAttachmentWrapper?.nativeElement?.offsetHeight;
    const buyerSupplierHeight = this.buyerSupplier?.nativeElement?.offsetHeight || 0;
    if (attachmentsElHeight > 0) {
      this.tableHeight = `calc(100vh - 318px - ${attachmentsElHeight + buyerSupplierHeight}px)`;
    }
  }

    ngOnDestroy(): void {
        this.updatedAttachments = null;
        this.signature = null;
    }
}
